'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useRouter } from 'next/navigation';
import { useIsMutating } from '@tanstack/react-query';
import { styled } from '@classify/react-core-components/styles';
import { Formik, Form, FieldArray } from 'formik';
import { FormControl, FormLabel } from '@mui/material';
import Button from '@classify/react-core-components/Button';
import Typography from '@classify/react-core-components/Typography';
import Checkbox from '@classify/react-core-components/Checkbox';
import Callout from '@classify/react-core-components/Callout';
import Tag from '@classify/react-core-components/Tag';
import { RichContent } from '@openclassrooms/rich-content';
import Snackbar from '@classify/react-core-components/Snackbar';
import { useSubmitExerciseSession } from '@openclassrooms/data-connect-web/assessment';
import { translate } from '@openclassrooms/translations';
import type { ExerciseSession } from '@openclassrooms/sdk-web';
import MultipleAnswerQuestion from './MultipleAnswerQuestion';
import SingleAnswerQuestion from './SingleAnswerQuestion';
const QuestionList = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: spacing(0, 2),
  [breakpoints.up('sm')]: {
    padding: spacing(0, 3)
  }
}));
const QuestionWrapper = styled(FormControl)(({
  theme: {
    spacing
  }
}) => ({
  maxWidth: '100%',
  '&:is(:last-child)': {
    marginBottom: spacing(3)
  },
  '&:not(:last-child)': {
    marginBottom: spacing(8)
  }
}));
const Legend = styled(FormLabel)({
  width: '100%'
});
const TitleWrapper = styled('div')(({
  theme: {
    spacing,
    palette
  }
}) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginBottom: spacing(2),
  paddingBottom: spacing(1),
  borderBottom: `1px solid ${(palette as any).line || palette.divider}`
}));
const QuestionContent = styled(RichContent)(({
  theme: {
    spacing,
    typography
  }
}) => ({
  marginBottom: spacing(2),
  '& > p': {
    ...typography.subtitle1
  },
  '& > pre, & > p': {
    margin: `${0} !important`
  },
  '& > pre > code': {
    margin: [spacing(3, 0, 1), '!important']
  }
}));
const Explanation = styled(RichContent)(({
  theme: {
    spacing
  }
}) => ({
  marginTop: spacing(1),
  '& > pre, & > p': {
    margin: `${0} !important`
  }
}));
const SubmitInformationWrapper = styled('div')(({
  theme: {
    spacing,
    palette,
    breakpoints
  }
}) => ({
  padding: spacing(4, 2),
  marginTop: spacing(1),
  borderTop: `1px solid ${(palette as any).stroke || palette.divider}`,
  [breakpoints.up('sm')]: {
    padding: spacing(4, 3)
  }
}));
function formatQuizSessionQuestions(questions: any[], values: Record<string, number[]>) {
  return questions.map(({
    id: questionId,
    answers
  }) => ({
    id: questionId,
    answers: answers.map(({
      id: answerId
    }) => ({
      id: answerId,
      selected: values[questionId]?.includes(answerId) ?? false,
      content: null
    }))
  }));
}
interface Props {
  exerciseId: number;
  exerciseSession: ExerciseSession;
  setExerciseSession: Dispatch<SetStateAction<ExerciseSession | null>>;
  refetchQuizInformation: () => void;
  setHasResetQuiz: Dispatch<SetStateAction<boolean>>;
}
const QuizQuestions = t0 => {
  const $ = _c(35);
  const {
    exerciseId,
    exerciseSession,
    setExerciseSession,
    refetchQuizInformation,
    setHasResetQuiz
  } = t0;
  const router = useRouter();
  const [isUserConsentChecked, setIsUserConsentChecked] = useState(false);
  const [hasSubmitError, setSubmitError] = useState(false);
  let t1;
  if ($[0] !== exerciseSession) {
    t1 = exerciseSession || {};
    $[0] = exerciseSession;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    questions,
    completedAt,
    exerciseSessionId,
    assessable: isAssessable
  } = t1;
  const [submitExerciseSession] = useSubmitExerciseSession();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mutationKey: ["quiz-session-question-answers"]
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isSavingAnswers = useIsMutating(t2) > 0;
  let t3;
  if ($[3] !== questions) {
    t3 = questions.filter(_temp2);
    $[3] = questions;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const questionsAnswered = t3;
  const remainingQuestionsTotalMemo = questions.length - questionsAnswered.length;
  const [remainingQuestionsTotal, setRemainingQuestionsTotal] = useState(remainingQuestionsTotalMemo);
  const isCompleted = Boolean(completedAt);
  let initialValues;
  if ($[5] !== questions) {
    initialValues = {};
    questions.forEach(t4 => {
      const {
        id,
        answers
      } = t4;
      initialValues[id] = answers.filter(_temp3).map(_temp4);
    });
    $[5] = questions;
    $[6] = initialValues;
  } else {
    initialValues = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = values => {
      const errors = {};
      const remainingQuestions = Object.values(values).filter(_temp5);
      if (remainingQuestions.length > 0) {
        errors.hasCompleted = false;
        setIsUserConsentChecked(false);
      }
      setRemainingQuestionsTotal(remainingQuestions.length);
      return errors;
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== exerciseId || $[9] !== exerciseSessionId || $[10] !== isAssessable || $[11] !== questions || $[12] !== refetchQuizInformation || $[13] !== router || $[14] !== setExerciseSession || $[15] !== setHasResetQuiz || $[16] !== submitExerciseSession) {
    t5 = (values_0, actions) => {
      const completedQuestions = formatQuizSessionQuestions(questions, values_0);
      const exerciseSessionAnswers = {
        id: exerciseId,
        assessable: isAssessable,
        questions: completedQuestions
      };
      submitExerciseSession({
        exerciseSessionId,
        exerciseSessionAnswers
      }, {
        onSettled() {
          actions.setSubmitting(false);
        },
        onSuccess(data) {
          setExerciseSession(data);
          refetchQuizInformation();
          setHasResetQuiz(false);
          router.refresh();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        },
        onError() {
          setSubmitError(true);
        }
      });
    };
    $[8] = exerciseId;
    $[9] = exerciseSessionId;
    $[10] = isAssessable;
    $[11] = questions;
    $[12] = refetchQuizInformation;
    $[13] = router;
    $[14] = setExerciseSession;
    $[15] = setHasResetQuiz;
    $[16] = submitExerciseSession;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== isCompleted || $[19] !== isSavingAnswers || $[20] !== isUserConsentChecked || $[21] !== questions || $[22] !== remainingQuestionsTotal) {
    t6 = formik => <Form><FieldArray name="quiz" render={() => <QuestionList>{questions.map((t7, index) => {
          const {
            answers: answers_1,
            content,
            id: questionId,
            isMultiple,
            explanation,
            mcqQuestionSessionId,
            correct
          } = t7;
          const questionName = translate("mcq.edit.message.question_title_num", {
            num: index + 1
          });
          const shouldDisplayExplanation = explanation && isCompleted;
          const hasAnsweredQuestion = correct !== null;
          return <QuestionWrapper component="fieldset" key={`question-${questionId}`}><Legend component="legend" color="secondary"><TitleWrapper><Typography variant="h4" component="p">{questionName}</Typography>{hasAnsweredQuestion && <Tag color={correct ? "success" : "danger"} sx={{
                  position: "absolute",
                  right: 0
                }}>{correct ? translate("quiz.correction.question.answer_correct") : translate("quiz.correction.question.answer_incorrect")}</Tag>}</TitleWrapper><QuestionContent content={content} /></Legend>{isMultiple ? <MultipleAnswerQuestion answers={answers_1} questionId={questionId} isCompleted={isCompleted} quizSessionQuestionId={mcqQuestionSessionId} /> : <SingleAnswerQuestion answers={answers_1} questionId={questionId} isCompleted={isCompleted} quizSessionQuestionId={mcqQuestionSessionId} />}{shouldDisplayExplanation && <Explanation content={explanation} />}</QuestionWrapper>;
        })}</QuestionList>} />{!isCompleted && <SubmitInformationWrapper>{remainingQuestionsTotal > 0 && <Callout type="info" sx={{
          mb: 2
        }}>{translate("course.chapter.quiz.remaining_questions.content_block", {
            number: remainingQuestionsTotal
          }, remainingQuestionsTotal)}</Callout>}{remainingQuestionsTotal === 0 && <Checkbox sx={{
          mb: 2
        }} name="quiz-consent" label={translate("course.chapter.quiz.submit.checkbox")} value={isUserConsentChecked} defaultChecked={false} onChange={e => setIsUserConsentChecked(e.target.checked)} />}<Button disabled={!isUserConsentChecked || isCompleted || isSavingAnswers} variant="primary" type="submit" isLoading={formik.isSubmitting}>{translate("global.word.validate")}</Button></SubmitInformationWrapper>}</Form>;
    $[18] = isCompleted;
    $[19] = isSavingAnswers;
    $[20] = isUserConsentChecked;
    $[21] = questions;
    $[22] = remainingQuestionsTotal;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== initialValues || $[25] !== t5 || $[26] !== t6) {
    t7 = <Formik initialValues={initialValues} validate={t4} onSubmit={t5}>{t6}</Formik>;
    $[24] = initialValues;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setSubmitError(false);
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = translate("global.error.general_error");
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== hasSubmitError) {
    t10 = <Snackbar type="error" isOpen={hasSubmitError} onClose={t8} autoHideDuration={5000} message={t9} />;
    $[30] = hasSubmitError;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t7) {
    t11 = <div>{t7}{t10}</div>;
    $[32] = t10;
    $[33] = t7;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};
export default QuizQuestions;
function _temp(answer) {
  return answer.selected;
}
function _temp2(question) {
  const questionsSelected = question.answers.filter(_temp);
  return questionsSelected.length > 0;
}
function _temp3(t0) {
  const {
    selected
  } = t0;
  return selected;
}
function _temp4(t0) {
  const {
    id: selectedAnswerId
  } = t0;
  return selectedAnswerId;
}
function _temp5(answers_0) {
  return (answers_0 as number[]).length === 0;
}